import { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Card, CircularProgress, Grid, Chip, Alert,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { MenuBook, AutoAwesome, TrendingUp, TrendingDown, Star } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => {
  // Token is stored as its own localStorage key at login (see Login.js), not on
  // the user object. Fall back to the user object for older sessions.
  const u = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token') || u?.token || u?.authToken || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Minimal markdown render for the guidance: bold headers, bullets, inline **bold**
const inlineBold = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
  /^\*\*[^*]+\*\*$/.test(part) ? <strong key={j}>{part.slice(2, -2)}</strong> : part);
const renderNarrative = (md = '') => md.split('\n').map((line, i) => {
  const t = line.trim();
  if (!t) return <Box key={i} sx={{ height: 6 }} />;
  const h = t.match(/^\*\*(.+?)\*\*:?$/);
  if (h) return <Typography key={i} variant="subtitle2" fontWeight={700} sx={{ mt: 1, color: '#4a148c' }}>{h[1]}</Typography>;
  if (/^[-*]\s+/.test(t)) return <Typography key={i} variant="body2" sx={{ ml: 1.5 }}>• {inlineBold(t.replace(/^[-*]\s+/, ''))}</Typography>;
  return <Typography key={i} variant="body2" sx={{ mt: 0.3 }}>{inlineBold(t)}</Typography>;
});

export default function ParentAcademics() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [pathway, setPathway] = useState(null);   // { visible, guidance } or null
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const lRes = await axios.get(`${API}/api/guardians/me/learners`, { headers: hdrs() });
        const kids = lRes.data?.data || [];
        setLearners(kids);
        if (kids.length > 0) {
          const l = kids[0];
          setSelectedLearner(l);
          loadAcademics(l.admNo);
        }
      } catch (e) {
        console.error('Error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user.email]);

  const loadAcademics = async (admNo) => {
    setPathway(null);
    try {
      const res = await axios.get(`${API}/api/academics/learner/${admNo}/subjects`, { headers: hdrs() });
      setSubjects(res.data || []);
    } catch (e) {
      console.error('Error loading academics:', e);
      setSubjects([]);
    }
    // Career-pathway guidance — only returns content when the school enabled it AND a teacher approved it
    try {
      const g = await axios.get(`${API}/api/academics/learner/${admNo}/pathway-guidance`, { headers: hdrs() });
      setPathway(g.data || null);
    } catch (e) {
      setPathway(null);
    }
  };

  if (loading) return <Box textAlign="center" py={8}><CircularProgress /></Box>;

  const avgScore = subjects.length > 0
    ? (subjects.reduce((s, subj) => s + (subj.score || 0), 0) / subjects.length).toFixed(1)
    : 0;

  // Pathway Insight: Identify strengths, weaknesses, and trends
  const generatePathwayInsight = () => {
    if (subjects.length === 0) return null;
    
    const sorted = [...subjects].sort((a, b) => (b.score || 0) - (a.score || 0));
    const strengths = sorted.slice(0, Math.max(2, Math.ceil(sorted.length * 0.3))); // Top 30%
    const concerns = sorted.slice(Math.max(0, sorted.length - Math.ceil(sorted.length * 0.3))); // Bottom 30%
    const passing = subjects.filter(s => s.passing).length;
    const atRisk = subjects.filter(s => !s.passing).length;
    const excellentScores = subjects.filter(s => (s.score || 0) >= 80).length;
    
    return {
      strengths,
      concerns,
      passingRate: Math.round((passing / subjects.length) * 100),
      excellentRate: Math.round((excellentScores / subjects.length) * 100),
      atRiskCount: atRisk,
      performanceLevel: avgScore >= 75 ? 'Strong' : avgScore >= 60 ? 'Average' : 'Needs Support'
    };
  };

  const insight = generatePathwayInsight();

  return (
    <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box py={{ xs: 2, sm: 3 }}>
        <Typography variant="h5" fontWeight={700} mb={3} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>📚 Academic Performance</Typography>

        <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '1rem' }, lineHeight: 1.5, wordBreak: 'break-word' }}>
          View your child's current academic subjects, performance, and grades.
        </Alert>

        {/* Learner Selector */}
        {learners.length > 1 && (
          <Box sx={{ mb: 3, display: 'flex', gap: 1, overflowX: 'auto', pb: 1, WebkitOverflowScrolling: 'touch' }}>
            {learners.map(l => (
              <Chip
                key={l._id}
                label={l.name}
                onClick={() => {
                  setSelectedLearner(l);
                  loadAcademics(l.admNo);
                }}
                color={selectedLearner?._id === l._id ? 'primary' : 'default'}
                variant={selectedLearner?._id === l._id ? 'filled' : 'outlined'}
                sx={{ flexShrink: 0 }}
              />
            ))}
          </Box>
        )}

        {/* Summary - Responsive Grid - 2x2 on Mobile */}
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ p: { xs: 1, sm: 1.5 }, textAlign: 'center', height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, fontWeight: 700, letterSpacing: 0.5 }}>
                SUBJECTS
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>{subjects.length}</Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ p: { xs: 1, sm: 1.5 }, textAlign: 'center', bgcolor: 'primary.50', height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <Typography variant="caption" color="primary.main" display="block" mb={0.5} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, fontWeight: 700, letterSpacing: 0.5 }}>
                AVERAGE
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>{avgScore}%</Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ p: { xs: 1, sm: 1.5 }, textAlign: 'center', height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, fontWeight: 700, letterSpacing: 0.5 }}>
                STATUS
              </Typography>
              <Chip
                label={selectedLearner?.status || 'Active'}
                size="small"
                color="success"
                sx={{ mt: 0.25, fontSize: { xs: '0.6rem', sm: '0.7rem' }, height: { xs: 20, sm: 22 } }}
              />
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ p: { xs: 1, sm: 1.5 }, textAlign: 'center', height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, fontWeight: 700, letterSpacing: 0.5 }}>
                GRADE
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>{selectedLearner?.stage || '—'}</Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Subjects - Card-based layout for mobile, Table for desktop */}
        {subjects.length === 0 ? (
          <Card sx={{ boxShadow: 1, borderRadius: 2, p: { xs: 2, sm: 3 }, textAlign: 'center', mb: 3 }}>
            <MenuBook sx={{ fontSize: { xs: 24, sm: 32 }, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>No subjects yet</Typography>
          </Card>
        ) : (
          <>
            {/* Mobile: Card View */}
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1.5, mb: 3 }}>
              {subjects.map((subj, i) => (
                <Card key={i} sx={{ p: 1.5, borderRadius: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #eee' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box flex={1}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.9rem' }, color: '#333' }}>{subj.name || subj.code}</Typography>
                    </Box>
                    <Chip
                      label={subj.grade || 'N/A'}
                      size="small"
                      color={subj.score >= 70 ? 'success' : subj.score >= 50 ? 'warning' : 'error'}
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 0.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 600, display: 'block', mb: 0.25 }}>SCORE</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.95rem', color: '#1976d2' }}>{subj.score || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 600, display: 'block', mb: 0.25 }}>STATUS</Typography>
                      <Chip
                        label={subj.passing ? 'Passing' : 'At Risk'}
                        size="small"
                        color={subj.passing ? 'success' : 'warning'}
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    </Box>
                  </Box>
                  {subj.teacher && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 600, display: 'block', mb: 0.25 }}>TEACHER</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666' }}>{subj.teacher}</Typography>
                    </Box>
                  )}
                </Card>
              ))}
            </Box>

            {/* Desktop: Table View */}
            <Card sx={{ boxShadow: 1, borderRadius: 2, overflow: 'hidden', mb: 3, display: { xs: 'none', sm: 'block' } }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1.5, px: 2 }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1.5, px: 2 }} align="center">Score</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1.5, px: 2 }} align="center">Grade</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1.5, px: 2 }} align="center">Teacher</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1.5, px: 2 }} align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subjects.map((subj, i) => (
                    <TableRow key={i} hover sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell sx={{ fontSize: '0.875rem', py: 1.2, px: 2, fontWeight: 600 }}>{subj.name || subj.code}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.875rem', py: 1.2, px: 2, fontWeight: 600 }}>{subj.score || '—'}</TableCell>
                      <TableCell align="center" sx={{ py: 1.2, px: 2 }}>
                        <Chip
                          label={subj.grade || 'N/A'}
                          size="small"
                          color={subj.score >= 70 ? 'success' : subj.score >= 50 ? 'warning' : 'error'}
                          variant="outlined"
                          sx={{ fontSize: '0.75rem', height: 24 }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.875rem', py: 1.2, px: 2 }}>{subj.teacher || '—'}</TableCell>
                      <TableCell align="center" sx={{ py: 1.2, px: 2 }}>
                        <Chip
                          label={subj.passing ? 'Passing' : 'At Risk'}
                          size="small"
                          color={subj.passing ? 'success' : 'warning'}
                          variant="outlined"
                          sx={{ fontSize: '0.75rem', height: 24 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </>
        )}

        {/* Pathway Insight: Performance Analysis */}
        {insight && (
          <Card sx={{ mb: 3, p: { xs: 1.2, sm: 2 }, borderRadius: 2, bgcolor: '#f3e5f5', border: '1px solid #ce93d8' }}>
            <Box display="flex" alignItems="center" gap={0.8} mb={1}>
              <Star sx={{ color: '#8e24aa', fontSize: { xs: 16, sm: 20 }, flexShrink: 0 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#8e24aa', fontSize: { xs: '0.85rem', sm: '1rem' } }}>Pathway Insight</Typography>
            </Box>

            {/* Performance Summary Grid - 2x2 */}
            <Grid container spacing={{ xs: 0.8, sm: 1 }} sx={{ mb: 1 }}>
              <Grid item xs={6}>
                <Box sx={{ p: 0.8, bgcolor: '#fff', borderRadius: 0.8, border: '1px solid #e0bee7', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem', fontWeight: 600, mb: 0.2 }}>Performance</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: insight.performanceLevel === 'Strong' ? '#2e7d32' : insight.performanceLevel === 'Average' ? '#f57f17' : '#c62828', fontSize: '0.75rem' }}>
                    {insight.performanceLevel}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ p: 0.8, bgcolor: '#fff', borderRadius: 0.8, border: '1px solid #e0bee7', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem', fontWeight: 600, mb: 0.2 }}>Passing</Typography>
                  <Typography variant="body2" fontWeight={700} color="success.main" sx={{ fontSize: '0.75rem' }}>{insight.passingRate}%</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ p: 0.8, bgcolor: '#fff', borderRadius: 0.8, border: '1px solid #e0bee7', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem', fontWeight: 600, mb: 0.2 }}>Excellent</Typography>
                  <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ fontSize: '0.75rem' }}>{insight.excellentRate}%</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ p: 0.8, bgcolor: '#fff', borderRadius: 0.8, border: '1px solid #e0bee7', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem', fontWeight: 600, mb: 0.2 }}>At Risk</Typography>
                  <Typography variant="body2" fontWeight={700} color={insight.atRiskCount > 0 ? 'warning.main' : 'success.main'} sx={{ fontSize: '0.75rem' }}>{insight.atRiskCount}</Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Strengths */}
            {insight.strengths.length > 0 && (
              <Box sx={{ mb: 0.8 }}>
                <Box display="flex" alignItems="center" gap={0.4} mb={0.4}>
                  <TrendingUp sx={{ fontSize: 14, color: '#2e7d32' }} />
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.65rem', color: '#2e7d32' }}>Strengths</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                  {insight.strengths.slice(0, 2).map((s, i) => (
                    <Chip
                      key={i}
                      label={`${s.name || s.code} (${s.score}%)`}
                      size="small"
                      variant="outlined"
                      color="success"
                      sx={{ fontSize: '0.6rem', height: 20 }}
                    />
                  ))}
                  {insight.strengths.length > 2 && (
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#2e7d32', fontWeight: 600, pt: 0.5 }}>+{insight.strengths.length - 2} more</Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Areas of Concern */}
            {insight.concerns.length > 0 && insight.concerns.some(c => !c.passing) && (
              <Box sx={{ mb: 0.8 }}>
                <Box display="flex" alignItems="center" gap={0.4} mb={0.4}>
                  <TrendingDown sx={{ fontSize: 14, color: '#c62828' }} />
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.65rem', color: '#c62828' }}>Areas to Focus</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                  {insight.concerns.filter(c => !c.passing).slice(0, 2).map((c, i) => (
                    <Chip
                      key={i}
                      label={`${c.name || c.code} (${c.score}%)`}
                      size="small"
                      variant="outlined"
                      color="error"
                      sx={{ fontSize: '0.6rem', height: 20 }}
                    />
                  ))}
                  {insight.concerns.filter(c => !c.passing).length > 2 && (
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#c62828', fontWeight: 600, pt: 0.5 }}>+{insight.concerns.filter(c => !c.passing).length - 2} more</Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Recommendation */}
            <Box sx={{ p: 0.8, bgcolor: '#fff', borderRadius: 0.8, border: '1px dashed #8e24aa', mt: 0.8 }}>
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.6rem', color: '#8e24aa', display: 'block', mb: 0.2 }}>💡 Recommendation</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.3 }}>
                {insight.passingRate >= 80 && insight.excellentRate >= 40 && 'Strong progress. Continue supporting current study habits.'}
                {insight.passingRate >= 80 && insight.excellentRate < 40 && 'Good foundation. Focus effort on challenging subjects.'}
                {insight.passingRate >= 60 && insight.passingRate < 80 && insight.atRiskCount > 0 && 'Some subjects need support. Discuss with class teacher.'}
                {insight.passingRate < 60 && 'Additional support needed. Schedule meeting with teachers.'}
              </Typography>
            </Box>
          </Card>
        )}

        {/* Career-pathway guidance (only when school-enabled AND teacher-approved) */}
        {pathway?.visible && pathway.guidance && (
          <Card sx={{ mb: 3, p: { xs: 1.5, sm: 2 }, borderRadius: 2, bgcolor: '#faf5ff', border: '1px solid #e1bee7' }}>
            <Box display="flex" alignItems="flex-start" gap={1} mb={1} flexWrap="wrap">
              <AutoAwesome sx={{ color: '#6a1b9a', fontSize: { xs: 16, sm: 20 }, flexShrink: 0, mt: 0.3 }} />
              <Box flex={1} minWidth={0}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#6a1b9a', fontSize: { xs: '0.95rem', sm: '1rem' }, mb: 0.5 }}>Career Pathway Guidance</Typography>
                {pathway.guidance.recommendedPathway && (
                  <Chip size="small" label={pathway.guidance.recommendedPathway}
                    sx={{ bgcolor: '#6a1b9a', color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }} />
                )}
              </Box>
            </Box>
            <Box sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, lineHeight: 1.6 }}>{renderNarrative(pathway.guidance.narrative)}</Box>
            {pathway.guidance.teacherRemarks && (
              <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff', borderRadius: 1, border: '1px solid #eee' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, display: 'block', mb: 0.5 }}>Teacher's note</Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{pathway.guidance.teacherRemarks}</Typography>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" display="block" mt={1.5} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, lineHeight: 1.5 }}>
              This is guidance to discuss with your child and their teacher — not a fixed decision.
              Interests and effort matter alongside these results.
            </Typography>
          </Card>
        )}

        {/* Notes */}
        {selectedLearner && (
          <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'info.50', borderRadius: 2, border: '1px solid rgba(25, 118, 210, 0.2)' }}>
            <Typography variant="caption" color="info.main" fontWeight={700} display="block" mb={1} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              📌 Notes
            </Typography>
            <Typography variant="body2" color="info.dark" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, lineHeight: 1.6 }}>
              For detailed performance analysis and teacher feedback, contact your child's class teacher.
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
}

